import { useState } from 'react';
import Icon from './Icon';
import ExpandCaret from './ExpandCaret';
import GateMark from './GateMark';
import { REVIEW_MARK_TITLE } from '../utils/gateMark';

// Sticky consolidated issue strip for the review/correction screens. Renders
// the issue list produced by
// utils/reviewIssues.deriveReviewIssues — the SAME derivation the inline cell
// flags use, so the strip and the cells can never disagree. It is the SINGLE
// place all review signals surface (row/price/discount/date/duplicate/
// recipient), replacing the separate standalone banners so "green = all clear"
// stays truthful.
//
// Collapsible to save vertical space: blocking errors force it open (you can't
// hide what stops a save); a warnings-only strip OPENS expanded too — collapsed
// by default it was a one-liner reviewers learned to skip (owner, #260) — and
// the operator can fold it to a one-line summary. Each chip click scrolls the offending field
// into view and focuses it; a chip may carry a secondary action (e.g. "compare
// to existing" for a duplicate), or several (`actions`, e.g. the total's
// התאם הנחה / התאם מע"מ), dispatched via onAction.
// `queueTitle` is the badge's tooltip when the label counts a supplier's run
// (#313); by default it says what the badge is.
// `gate` is the draft gate's answer for the draft on screen (stage 3 / C,
// #332): POST /documents/:id/gate-view's { gate_state, gate_color,
// gate_reasons }, never a listed or cached colour. Its mark leads the head and
// stays as it was at opening; the live chips under it are the truth. The
// gate's own extra reasons arrive inside `issues` (utils/gateMark.js
// gateStripIssues), so the green all-clear never shows while one is open.
// `hideAllClear` drops that all-clear where it would mislead with no issue to
// raise: a return created with no rows yet (#367, owner 02/11/2026).
export default function ReviewIssueSummary({ issues = [], queueInfo = null, queueTitle, onAction, gate = null, hideAllClear = false }) {
  const errors   = issues.filter(i => i.severity === 'error');
  const warnings = issues.filter(i => i.severity !== 'error');
  const clean = issues.length === 0;

  // Warnings-only strips start expanded and may be folded; blocking errors
  // are always expanded.
  const [userCollapsed, setUserCollapsed] = useState(false);
  const collapsible = !clean && errors.length === 0;
  const expanded = errors.length > 0 || !collapsible ? true : !userCollapsed;
  const showChips = !clean && expanded;

  const focusIssue = (issue) => {
    if (!issue.targetId) return;
    const el = document.getElementById(issue.targetId);
    if (!el) return;
    el.scrollIntoView({ behavior: 'smooth', block: 'center' });
    if (typeof el.focus === 'function') el.focus({ preventScroll: true });
    el.classList.add('ri-flash');
    setTimeout(() => el.classList.remove('ri-flash'), 1400);
  };

  const errLabel  = errors.length === 1 ? 'תקלה חוסמת' : `${errors.length} חוסמות`;
  const warnLabel = warnings.length === 1 ? 'אזהרה אחת' : `${warnings.length} אזהרות`;
  const allClearHidden = clean && hideAllClear;
  if (allClearHidden && !gate && !queueInfo) return null;
  const summary = allClearHidden ? null : clean ? (
    <span style={{ fontWeight: 700, color: 'var(--success-ink)', display: 'inline-flex', alignItems: 'center', gap: 5 }}><Icon name="check-circle" size={15} /> אין תקלות פתוחות</span>
  ) : (
    <span style={{ fontWeight: 700 }}>
      {collapsible && <span className="review-issues-chevron"><ExpandCaret open={expanded} size={12} /></span>}
      {errors.length > 0 && <span style={{ color: 'var(--danger-ink)' }}>{errLabel}</span>}
      {errors.length > 0 && warnings.length > 0 && <span style={{ color: 'var(--ink-muted)' }}> · </span>}
      {warnings.length > 0 && <span style={{ color: 'var(--warning-ink)' }}>{warnLabel}</span>}
    </span>
  );

  return (
    <div className={`review-issues-strip ${allClearHidden ? '' : clean ? 'clean' : (errors.length > 0 ? 'has-errors' : 'has-warnings')}`} dir="rtl">
      <div className="review-issues-head">
        <span className="review-issues-lead">
        {gate && <GateMark gate={gate} title={REVIEW_MARK_TITLE} />}
        {collapsible ? (
          <button
            type="button"
            className="review-issues-summary-btn"
            aria-expanded={expanded}
            onClick={() => setUserCollapsed(c => !c)}
            title={expanded ? 'הסתר פירוט' : 'הצג פירוט'}
          >
            {summary}
          </button>
        ) : summary}
        </span>
        {queueInfo && (
          <span className="review-queue-badge" title={queueTitle || 'מיקום בתור הטיוטות'}>{queueInfo}</span>
        )}
      </div>
      {showChips && (
        <div className="review-issues-chips">
          {issues.map(issue => (
            <span key={issue.id} className={`review-issue-chip ${issue.severity === 'error' ? 'err' : 'warn'}`}>
              <button
                type="button"
                className="review-issue-chip-main"
                onClick={() => focusIssue(issue)}
                title={issue.targetId ? 'לחיצה תקפוץ לשדה הבעייתי' : undefined}
                style={issue.targetId ? undefined : { cursor: 'default' }}
              >
                <Icon name={issue.severity === 'error' ? 'x-circle' : 'alert'} size={13} /> {issue.label}
              </button>
              {onAction && (issue.actions || (issue.action ? [issue.action] : [])).map(a => (
                <button
                  key={a.kind}
                  type="button"
                  className="review-issue-chip-action"
                  onClick={() => onAction(a)}
                >
                  {a.label}
                </button>
              ))}
            </span>
          ))}
        </div>
      )}
    </div>
  );
}
